/**
 * Migrations page (/eng/migrations): every one-off data update in the code sf-api runs here,
 * whether it has run on this environment, and Dry run / Run. Needs eng:migrations, like every
 * sf-api /admin/eng/migrations route; the server is the real check (dry run first, one at a time),
 * the page only avoids offering what it would refuse.
 */
import { useEffect, useRef, useState } from 'react';
import { usePermissions } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Modal, Result, Space, Spin, Typography } from 'antd';
import { hasPermission } from '../../../utils/permissions';
import { APP_ENVIRONMENT } from '../../../config/env';
import { EnvironmentTag } from '../../demos/components/EnvironmentBadge';
import { environmentLabel } from '../../demos/constants';
import { MIGRATIONS_PERMISSION, modeLabel, type RunMode } from './constants';
import {
  migrationErrorMessage,
  refreshMigrationRuns,
  migrationsQueryKey,
  startMigrationArgs,
  useMigrationMutation,
  useMigrations,
  type Migration,
  type MigrationRun,
} from './api/migrationsClient';
import { MigrationsTable } from './components/MigrationsTable';
import { MigrationRunsDrawer } from './components/MigrationRunsDrawer';
import { RemovedMigrationsTable } from './components/RemovedMigrationsTable';
import { lastRunOf, runnerName } from './format';
import { formatTime } from '../envCopy/format';
import { useCopyStartScope } from '../envCopy/api/envCopyClient';
import { EditScopeNotice } from '../envCopy/components/EditScopeNotice';

const { Title, Text, Paragraph } = Typography;

const Gate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: permissions, isLoading } = usePermissions<string[]>();
  if (isLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }
  if (!hasPermission(permissions ?? [], MIGRATIONS_PERMISSION)) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="Migrations need the eng:migrations permission."
      />
    );
  }
  return <>{children}</>;
};

const RunningBanner: React.FC<{ run: MigrationRun }> = ({ run }) => (
  <Alert
    type="info"
    showIcon
    message={`${run.migrationId} is running (${modeLabel(run.mode)})`}
    description={`Started by ${runnerName(run)} at ${formatTime(run.startedAt)}. Dry run and Run are off until it finishes.`}
  />
);

const MigrationsHome: React.FC = () => {
  const queryClient = useQueryClient();
  const query = useMigrations();
  // sf-api lets only staff who may change the whole platform start a migration.
  const scope = useCopyStartScope();
  const { mutate, isLoading: starting } = useMigrationMutation<MigrationRun>();
  // The consent is for this migration at this code version; its row is read from the current list.
  const [confirming, setConfirming] = useState<{ id: string; codeVersion: string } | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmProblem, setConfirmProblem] = useState<string | null>(null);

  const list = query.data?.data;
  const migrations = Array.isArray(list?.migrations) ? list.migrations : undefined;
  const removed = list?.removed ?? [];
  const running = list?.running ?? null;
  // Off while a run is in progress, a start is being sent, the list is loading or stale, or the
  // caller's edit scope is not settled as the whole platform.
  const actionsOff = !!running || starting || query.isFetching || query.isError || !scope.canStart;
  const confirmRow = confirming ? migrations?.find((m) => m.id === confirming.id) : undefined;

  // A row that left the list, or now holds other code, needs fresh consent.
  useEffect(() => {
    if (!confirming || !migrations) return;
    if (confirmRow && confirmRow.codeVersion === confirming.codeVersion) return;
    setConfirming(null);
    setProblem(`${confirming.id} changed since you chose Run. Check it again before running it.`);
  }, [confirming, confirmRow, migrations]);

  // The list polls; when it shows the open migration's last run changed (a new run, or one that
  // finished), read that history again so the drawer never stays on an older answer.
  const openRow = openId ? migrations?.find((m) => m.id === openId) : undefined;
  const openLast = openRow ? lastRunOf(openRow) : null;
  const openSignature = openLast ? `${openLast.runId}:${openLast.status}` : '';
  const seen = useRef<{ id: string | null; signature: string }>({ id: null, signature: '' });
  useEffect(() => {
    if (!openId || !openRow) return;
    const before = seen.current;
    seen.current = { id: openId, signature: openSignature };
    if (before.id === openId && before.signature !== openSignature) {
      void refreshMigrationRuns(queryClient, openId);
    }
  }, [openId, openRow, openSignature, queryClient]);

  const start = (m: Migration, mode: RunMode) => {
    setProblem(null);
    setConfirmProblem(null);
    mutate(startMigrationArgs(m.id, mode), {
      onSuccess: () => {
        setConfirming(null);
        void queryClient.invalidateQueries({ queryKey: [...migrationsQueryKey] });
        void refreshMigrationRuns(queryClient, m.id);
      },
      onError: (error) => {
        const verb = mode === 'apply' ? 'run' : 'dry run';
        const message = migrationErrorMessage(error, `Could not start the ${verb}. Try again.`);
        if (mode === 'apply') setConfirmProblem(message);
        else setProblem(message);
        // A refusal can mean the list is out of date (another run started, a dry run is stale).
        void queryClient.invalidateQueries({ queryKey: [...migrationsQueryKey] });
      },
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Space align="center">
          <Title level={3} style={{ marginBottom: 0 }}>
            Migrations
          </Title>
          <EnvironmentTag env={APP_ENVIRONMENT} />
        </Space>
        <div>
          <Text type="secondary">
            One-off data updates in the code running on {environmentLabel(APP_ENVIRONMENT)}. Dry run
            one first to see what it would change; it writes nothing.
          </Text>
        </div>
      </div>
      {running && <RunningBanner run={running} />}
      <EditScopeNotice scope={scope} action="starting a migration" />
      {problem && (
        <Alert type="error" showIcon closable message={problem} onClose={() => setProblem(null)} />
      )}
      <Card
        extra={
          <Button size="small" loading={query.isFetching} onClick={() => void query.refetch()}>
            Refresh
          </Button>
        }
      >
        {query.isError && !migrations ? (
          <Space direction="vertical">
            <Alert
              type="error"
              showIcon
              message={migrationErrorMessage(query.error, 'Could not load the migrations.')}
            />
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </Space>
        ) : (
          <>
            {query.isError && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message="Could not refresh. Showing the migrations last loaded; actions are off until they load."
              />
            )}
            <MigrationsTable
              migrations={migrations ?? []}
              loading={query.isLoading}
              actionsOff={actionsOff}
              onDryRun={(m) => start(m, 'dry_run')}
              onRun={(m) => {
                setConfirmProblem(null);
                setConfirming({ id: m.id, codeVersion: m.codeVersion });
              }}
              onOpen={(m) => setOpenId(m.id)}
            />
          </>
        )}
      </Card>

      {removed.length > 0 && (
        <Card>
          <RemovedMigrationsTable removed={removed} onOpen={(m) => setOpenId(m.id)} />
        </Card>
      )}

      <Modal
        open={!!confirming}
        title={confirmRow ? `Run ${confirmRow.ticket ?? confirmRow.id}?` : ''}
        onCancel={() => setConfirming(null)}
        footer={[
          <Button key="cancel" onClick={() => setConfirming(null)}>
            Cancel
          </Button>,
          <Button
            key="run"
            type="primary"
            danger
            loading={starting}
            disabled={!confirmRow || !confirmRow.canApply || actionsOff}
            onClick={() => confirmRow && start(confirmRow, 'apply')}
          >
            Run it
          </Button>,
        ]}
      >
        {confirmRow && (
          <>
            <Paragraph>
              {confirmRow.id}: {confirmRow.description}
            </Paragraph>
            <Paragraph>
              This changes data on {environmentLabel(APP_ENVIRONMENT)}. It runs in the background;
              this page shows when it finishes.
            </Paragraph>
          </>
        )}
        {confirmProblem && <Alert type="error" showIcon message={confirmProblem} />}
      </Modal>

      <MigrationRunsDrawer id={openId} onClose={() => setOpenId(null)} />
    </Space>
  );
};

export const MigrationsPage: React.FC = () => (
  <Gate>
    <MigrationsHome />
  </Gate>
);
